import { useState, useEffect, useMemo, useRef } from 'react'

import { Loading } from './../../../../Common/Loading'
import { Search } from './../../../../Common/Tables/Search'

import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { LogErrors } from './../../../../../utilities/LogErrors'

import { GetAllDataGroups as BLGetAllDataGroups, GetZtudentsOfGroup as BLGetZtudentsOfGroup, GetCurrentGroup as BLGetCurrentGroup } from './../../../../../utilities/BL/Instructor/Groups'
import { InviteZtudentsToGroup as BLInviteZtudentsToGroup } from './../../../../../utilities/BL/Instructor/ChangeGroup'

import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'

import { Confirm } from './../../../../Common/Modals/Confirm'
import { Details } from './../../../../Common/Modals/Details'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from '../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'


// Invitar desde otro grupo (Instructor)
// Estructura basada en ChangeGroup.js, simplificada: el grupo DESTINO es fijo (el grupo
// desde el que se llegó, vía GetCurrentGroup), así que solo hace falta elegir el grupo
// ORIGEN de donde se invitarán Ztudiantes. A diferencia de mover, "invitar" (endpoint
// InviteFromAnotherGroup) los agrega al grupo destino SIN quitarlos del origen, y acepta
// el lote completo de Ztudiantes seleccionados en una sola petición (no una por cada
// quien), regresando un outcome individual por cada idZtudiante.

/**
 * Normaliza el response de grupos (activos y archivados) a un solo arreglo.
 */
const normalizeGroups = (resp) => {
  if (!resp?.success) return []
  return [
    ...(resp?.data?.activos ?? []).map(x => ({ ...x, visible: true })),
    ...(resp?.data?.archivados ?? []).map(x => ({ ...x, visible: false })),
  ]
}

const actions = {
  INVITE: 'INVITE'
}

const InviteFromGroup = () => {
  const { showMessage } = useFloatMessage()

  // Estado general
  const [isLoading, setIsLoading] = useState(true)
  const [isWorking, setIsWorking] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  // Grupo destino (fijo: el grupo actual)
  const [destinationGroup, setDestinationGroup] = useState(null)

  // Grupos de origen (de donde se invita)
  const [groupsFrom, setGroupsFrom] = useState([])
  const [selectedGroupFromId, setSelectedGroupFromId] = useState(0)
  const [isGroupsFromLoading, setIsGroupsFromLoading] = useState(false)

  // Tabla de Ztudiantes (de grupo origen)
  const [allRows, setAllRows] = useState([])            // listado completo
  const [searchText, setSearchText] = useState('')      // texto del buscador
  const [selectedIds, setSelectedIds] = useState([])    // ids seleccionados para acción masiva

  const allRowsRef = useRef([])
  useEffect(() => {
    allRowsRef.current = allRows
  }, [allRows])

  // Modal de confirmación para acción individual
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [ztudentConfirmDetails, setZtudentConfirmDetails] = useState(null)
  const [actionTypeOfModal, setActionTypeOfModal] = useState(null)

  // Modal de resumen para acción masiva
  const [massiveSummary, setMassiveSummary] = useState(null)
  const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)

  // -------------------------
  // BÚSQUEDA + TABLA
  // -------------------------

  const processedRows = useMemo(() => {
    const text = (searchText ?? '').toLowerCase()
    if (!text) return allRows

    return allRows.filter(r => {
      const fields = [
        `${r?.name ?? ''} ${r?.lastName ?? ''}`,
        r?.user
      ]
      return fields.some(v => String(v ?? '').toLowerCase().includes(text))
    })
  }, [allRows, searchText])

  const isRowSelected = (idZtudiante) => selectedIds.includes(idZtudiante)

  const toggleRowSelect = (idZtudiante) => {
    setSelectedIds(prev =>
      prev.includes(idZtudiante)
        ? prev.filter(id => id !== idZtudiante)
        : [...prev, idZtudiante]
    )
  }

  const clearSelections = () => {
    setSelectedIds([])
  }

  const getSelectedZtudentsByIds = (ids) => {
    return ids
      .map(id => allRowsRef.current.find(z => z.idZtudiante === id))
      .filter(Boolean)
  }

  // -------------------------
  // CARGA INICIAL
  // -------------------------

  /**
   * Carga inicial de la página:
   * - Obtiene el grupo destino (fijo, el grupo actual).
   * - Obtiene el listado de grupos origen (excluyendo el grupo actual).
   */
  const LoadPage = async () => {
    try {
      setIsLoading(true)
      setIsWorking(true)
      setIsGroupsFromLoading(true)
      setErrorMessage(null)

      const responseGroup = await BLGetCurrentGroup()
      if (!responseGroup?.success) {
        setErrorMessage(responseGroup?.message ?? 'No ha sido posible obtener la información del grupo actual.')
        return
      }
      setDestinationGroup(responseGroup.data)

      const response = await BLGetAllDataGroups()
      if (response?.success) {
        // Solo se muestran los grupos activos como origen, excluyendo el grupo actual
        // (el destino), ya que no tiene sentido invitar desde el mismo grupo.
        const groups = normalizeGroups(response)
          .filter(g => g.visible)
          .filter(g => g.idGroup !== responseGroup.data?.id)
        setGroupsFrom(groups)
      } else {
        await LogErrors('LoadPage - InviteFromGroup (response not success)', response)
      }
    } catch (error) {
      await LogErrors('LoadPage - InviteFromGroup', error)
    } finally {
      setIsGroupsFromLoading(false)
      setIsWorking(false)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    LoadPage()
  }, [])

  // -------------------------
  // MANEJO DEL SELECT DE GRUPO ORIGEN
  // -------------------------

  const handleSelectGroupFrom = (groupId) => {
    const id = parseInt(groupId, 10) || 0
    setSelectedGroupFromId(id)

    // Limpiar tabla y filtros
    setAllRows([])
    setSearchText('')
    clearSelections()
  }

  // -------------------------
  // CARGAR ZTUDIANTES DEL GRUPO ORIGEN
  // -------------------------

  const LoadZtudentsTable = async () => {
    try {
      if (!selectedGroupFromId || selectedGroupFromId === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona primero un grupo de origen.'
        })
        return
      }

      setIsWorking(true)
      setErrorMessage(null)
      setAllRows([])
      setSearchText('')
      clearSelections()

      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: 'Cargando listado...'
      })

      const groupData = { id: selectedGroupFromId }
      const response = await BLGetZtudentsOfGroup(groupData)

      if (response?.success) {
        const students = Array.isArray(response.data)
          ? response.data
          : (response.data?.ztudiantes ?? [])

        const rows = (students ?? []).map(s => ({
          idZtudiante: s.idZtudiante ?? s.id ?? s.studentId,
          name: s.name ?? '',
          lastName: s.lastName ?? '',
          user: s.user ?? s.email ?? ''
        }))

        setAllRows(rows)

        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: 'Listado cargado con éxito'
        })
      } else {
        setAllRows([])
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: response?.message ?? 'No ha sido posible obtener el listado de Ztudiantes.'
        })
      }
    } catch (error) {
      await LogErrors('LoadZtudentsTable - InviteFromGroup', error)
      setAllRows([])
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar obtener el listado de Ztudiantes.'
      })
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // ACCIÓN INDIVIDUAL: INVITAR UN ZTUDIANTE
  // -------------------------

  const ShowConfirmInviteModal = (ztudent) => {
    if (!ztudent) return

    setActionTypeOfModal(actions.INVITE)
    setZtudentConfirmDetails(ztudent)
    setShowConfirmationModal(true)
  }

  /**
   * Ejecuta la invitación de un solo Ztudiante (llama al endpoint con un lote de 1).
   * Aunque la petición HTTP sea exitosa, se revisa el outcome específico de este
   * idZtudiante en response.data.results antes de darlo por invitado.
   */
  const InviteSingleZtudent = async () => {
    try {
      if (!ztudentConfirmDetails) return

      setIsWorking(true)

      const response = await BLInviteZtudentsToGroup(
        [ztudentConfirmDetails.idZtudiante],
        selectedGroupFromId,
        destinationGroup?.id
      )

      if (!response?.success) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: response?.message ?? 'No fue posible invitar al Ztudiante.'
        })
        // El modal (Confirm) solo cierra cuando success es true; se regresa la respuesta
        // tal cual para que se quede abierto y muestre el error también ahí.
        return response
      }

      const result = response.data?.results?.find(r => r.idZtudiante === ztudentConfirmDetails.idZtudiante)

      if (result?.outcome !== 'Invited') {
        const message = result?.errorMessage || 'No fue posible invitar al Ztudiante.'
        showMessage({ type: MESSAGE_TYPE.BAD, text: message })
        return { success: false, message }
      }

      showMessage({
        type: MESSAGE_TYPE.SUCCESS,
        text: `Se ha invitado al Ztudiante '${ztudentConfirmDetails.name} ${ztudentConfirmDetails.lastName}' al grupo '${destinationGroup?.name ?? ''}' de manera exitosa.`
      })

      // Quitar de la tabla local al Ztudiante ya invitado
      const updatedRows = allRowsRef.current.filter(
        z => z.idZtudiante !== ztudentConfirmDetails.idZtudiante
      )
      setAllRows(updatedRows)

      return { success: true }
    } catch (error) {
      await LogErrors('InviteSingleZtudent - InviteFromGroup', error)
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar invitar al Ztudiante.'
      })
      return { success: false, message: '' }
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // ACCIÓN MASIVA: INVITAR VARIOS ZTUDIANTES
  // -------------------------

  const HandleMassiveInviteZtudents = async () => {
    try {
      if (!selectedGroupFromId || selectedGroupFromId === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona primero el grupo de origen.'
        })
        return
      }

      if (!selectedIds || selectedIds.length === 0) {
        showMessage({
          type: MESSAGE_TYPE.BAD,
          text: 'Selecciona al menos un Ztudiante para invitar.'
        })
        return
      }

      setIsWorking(true)
      setErrorMessage(null)

      const selectedZtudents = getSelectedZtudentsByIds(selectedIds)
      const resumen = {
        successful: [],
        wrongs: [],
        omitteds: []
      }

      // A diferencia de mover (una petición por Ztudiante), este endpoint acepta el lote
      // completo en una sola llamada y regresa un outcome individual por cada uno.
      const stillExistingZtudents = selectedZtudents.filter(ztudent =>
        allRowsRef.current.some(z => z.idZtudiante === ztudent.idZtudiante)
      )

      selectedZtudents.forEach(ztudent => {
        if (!stillExistingZtudents.some(z => z.idZtudiante === ztudent.idZtudiante)) {
          resumen.omitteds.push({
            ...ztudent,
            reason: 'Ya no existe en el listado'
          })
        }
      })

      let currentRows = [...allRowsRef.current]

      if (stillExistingZtudents.length > 0) {
        try {
          const response = await BLInviteZtudentsToGroup(
            stillExistingZtudents.map(ztudent => ztudent.idZtudiante),
            selectedGroupFromId,
            destinationGroup?.id
          )

          if (response?.success) {
            const results = response.data?.results ?? []

            stillExistingZtudents.forEach(ztudent => {
              const result = results.find(r => r.idZtudiante === ztudent.idZtudiante)

              if (result?.outcome === 'Invited') {
                resumen.successful.push(ztudent)
                currentRows = currentRows.filter(z => z.idZtudiante !== ztudent.idZtudiante)
              } else {
                resumen.wrongs.push({
                  ...ztudent,
                  error: result?.errorMessage || 'Error desconocido al invitar al Ztudiante.'
                })
              }
            })
          } else {
            // La petición completa falló: se marcan todos como fallidos con el mismo mensaje.
            stillExistingZtudents.forEach(ztudent => {
              resumen.wrongs.push({
                ...ztudent,
                error: response?.message ?? 'Error desconocido al invitar al Ztudiante.'
              })
            })
          }
        } catch (error) {
          stillExistingZtudents.forEach(ztudent => {
            resumen.wrongs.push({
              ...ztudent,
              error: error.message ?? 'Error interno al invitar al Ztudiante.'
            })
          })
        }
      }

      setAllRows(currentRows)
      clearSelections()

      setMassiveSummary(resumen)
      setActionTypeOfModal(actions.INVITE)
      setShowMassiveSummaryModal(true)
    } catch (error) {
      await LogErrors('HandleMassiveInviteZtudents - InviteFromGroup', error)
      showMessage({
        type: MESSAGE_TYPE.BAD,
        text: 'Ha habido un error al intentar invitar a los Ztudiantes.'
      })
    } finally {
      setIsWorking(false)
    }
  }

  // -------------------------
  // RENDER
  // -------------------------

  return (
    isLoading ? (
      <Loading />
    ) : (
      <InstructorBase urlToGo={'/instructor/groups/group/'} idHelp={HELP_TYPE.INSTRUCTOR.GROUP} page={WEBPAGES.INSTRUCTOR.GROUP_INVITE} role={ROLES.INSTRUCTOR}>
        {isWorking && <Loading />}

        <section className='container Instructor__Reports'>
          <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
            Invitar Ztudiante desde otro grupo
          </p>

          {destinationGroup && (
            <p className='text-center mt-4'>Grupo destino: <strong>{destinationGroup.name}</strong></p>
          )}

          <p className='text-center mt-4'>Selecciona el grupo origen:</p>
          <div className='Instructor__ChangeGroup__Reports__Filters'>
            {isGroupsFromLoading ? (
              <select className='Instructor__ChangeGroup__Reports__Filters__List' disabled={true}>
                <option>Cargando lista...</option>
              </select>
            ) : (
              <select
                className='Instructor__ChangeGroup__Reports__Filters__List'
                value={selectedGroupFromId}
                onChange={(e) => handleSelectGroupFrom(e.target.value)}
                disabled={isWorking || groupsFrom.length === 0}
              >
                <option value={0}>Selecciona un grupo</option>
                {groupsFrom.map(g => (
                  <option key={g.idGroup} value={g.idGroup}>
                    {g.groupName}
                  </option>
                ))}
              </select>
            )}

            <button
              type='button'
              className='Btn__Ztudia--PrimaryOutline Instructor__ChangeGroup__Reports__Filters__Button'
              onClick={LoadZtudentsTable}
              disabled={isWorking || !selectedGroupFromId || selectedGroupFromId === 0}
            >
              Mostrar Ztudiantes
            </button>
          </div>

          {/* Buscador */}
          <Search
            onChange={setSearchText}
            extraClassName={'Instructor__Reports__Ztudents__Search mt-5 mw-100'}
          />

          {/* Tabla con listado de Ztudiantes del grupo */}
          <section className='Tables__Container Instructor__Progress__ContainerTable Tables--Shadow Tables--Background'>
            {selectedIds.length > 0 && (
                <div className='container Instructor__SpaceUp'>
                    <div className='Tables__ExtraButtons'>
                        <button
                        className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                        onClick={HandleMassiveInviteZtudents}
                        disabled={isWorking}
                        >
                        Invitar {selectedIds.length} Ztudiante(s) ahora
                        </button>
                    </div>

                    <div className='Tables__Table__RegistersSelected'>
                        <button
                        className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                        onClick={clearSelections}
                        >
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                        </button>

                        <div className='Tables__Table__RegistersSelected__Text'>
                        {selectedIds.length} ztudiante(s) seleccionada(s)
                        </div>

                        <button
                        className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                        onClick={clearSelections}
                        >
                        <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                        <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                        </button>
                    </div>
                </div>
            )}

            <table className='Tables__Table Instructor__Progress__ContainerTable__Table'>
              <thead>
                <tr className='Tables__Table__Row--Header'>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Progress__ContainerTable__Table__InputColumn'>
                    <input
                      type='checkbox'
                      className='Tables__Table__InputColumn__Input'
                      onChange={(e) => {
                        const newIds = e.target.checked
                          ? processedRows.map(row => row.idZtudiante)
                          : []
                        setSelectedIds(newIds)
                      }}
                      checked={
                        processedRows.length > 0 &&
                        processedRows.every(row => selectedIds.includes(row.idZtudiante))
                      }
                    />
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Nombre(s)
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Apellido(s)
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                    Usuario
                  </td>
                  <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Progress__ContainerTable__Table__ActionButtonsColumn'></td>
                </tr>
              </thead>
              <tbody>
                {processedRows?.length ? (
                  processedRows.map((r, idx) => (
                    <tr key={r.idZtudiante ?? idx} className='Tables__Table__Row'>
                      <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Progress__ContainerTable__Table__InputColumn'>
                        <input
                          type='checkbox'
                          className='Tables__Table__InputColumn__Input'
                          checked={isRowSelected(r.idZtudiante)}
                          onChange={() => toggleRowSelect(r.idZtudiante)}
                        />
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {`${r.name ?? ''}`.trim()}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {`${r.lastName ?? ''}`.trim()}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Progress__ContainerTable__Table__TextColumn'>
                        {r.user}
                      </td>
                      <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Progress__ContainerTable__Table__ActionButtonsColumn'>
                        <div className='Instructor__Progress__ContainerTable__Table__ActionButtonsColumn__Buttons'>
                          <button
                            type='button'
                            className='Btn__Ztudia--PrimaryOutline Instructor__Progress__ContainerTable__Table__ActionButtonsColumn__Buttons__Button'
                            onClick={() => ShowConfirmInviteModal(r)}
                            disabled={isWorking}
                          >
                            Invitar ahora
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className='Tables__Table__Row'>
                    <td className='Tables__Table__Row__Cell' colSpan={5}>
                      {isWorking ? 'Cargando...' : 'Sin resultados por mostrar'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          {errorMessage && errorMessage !== '' && (
            <p className='Instructor__Message--Error'>{errorMessage}</p>
          )}

          {/* Modal de confirmación para acción individual */}
          {showConfirmationModal && ztudentConfirmDetails && actionTypeOfModal === actions.INVITE && (
            <Confirm
              message={(() => {
                const originGroup = groupsFrom.find(g => g.idGroup === selectedGroupFromId)
                return `¿Estás segur@ de invitar al Ztudiante ${ztudentConfirmDetails?.name} ${ztudentConfirmDetails?.lastName} (${ztudentConfirmDetails?.user}) del grupo '${originGroup?.groupName ?? ''}' al grupo '${destinationGroup?.name ?? ''}'?`
              })()}
              actionToYesButton={InviteSingleZtudent}
              textYesButton={'Sí, invitar Ztudiante'}
              textNoButton={'No'}
              setShowModal={setShowConfirmationModal}
              setConfirmData={setZtudentConfirmDetails}
            />
          )}

          {/* Modal de resumen para acción masiva */}
          {showMassiveSummaryModal && massiveSummary && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={true}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowMassiveSummaryModal}
              setDetailsData={setMassiveSummary}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>
                  Resumen de Ztudiantes invitados
                </p>

                {massiveSummary ? (
                  <>
                    {massiveSummary.successful.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-check-circle'></i> Ztudiantes invitados:
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.successful.map((success, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {success.name} {success.lastName} ({success.user})
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.wrongs.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-x-circle'></i> Error al invitarlo(s):
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.wrongs.map((wrong, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {wrong.name} {wrong.lastName} ({wrong.user}) – {wrong.error}
                            </p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.omitteds.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-skip-end-btn'></i> Omitidos:
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.omitteds.map((omitted, index) => (
                            <p
                              key={index}
                              className='Modal__Layout--Summary__TextContainer__Text'
                            >
                              {omitted.name} {omitted.lastName} ({omitted.user})
                              {omitted.reason && ` – ${omitted.reason}`}
                            </p>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className='Modal__Layout--Summary__Text'>
                      No ha sido posible mostrar el resumen de lo realizado.
                    </p>
                  </>
                )}
              </div>
            </Details>
          )}
        </section>
      </InstructorBase>
    )
  )
}

export { InviteFromGroup }
